import React, { useState, useEffect, useRef } from 'react';
import { Play, Send, Loader2, CheckCircle, ExternalLink, Clock, AlertTriangle, Eye, XCircle, ChevronDown } from 'lucide-react';
import { otaTimerAPI } from '../services/api';
import { formatDateTime } from '../utils/datetime';

/**
 * CX Listing Workflow control.
 *
 * Two flow variants (iter253 Round 1):
 *   • Agoda / Expedia / MMT: Start → Under Review → Start (2nd) → Complete/Reject
 *   • Airbnb (and any OTA without an under-review stage): Start → Complete/Reject
 *
 * States:
 *   pending → in_progress (first_start)
 *           → under_review → in_progress (second_start)
 *           → completed | rejected
 */
const STATUS_META = {
  under_review: { label: 'Under Review', cls: 'bg-amber-100 text-amber-700', Icon: Eye },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700', Icon: XCircle },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700', Icon: Clock },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-700', Icon: CheckCircle },
};

const deriveStatus = (ota) => {
  const s = (ota.listing_status || '').toLowerCase();
  if (['pending', 'in_progress', 'under_review', 'completed', 'rejected'].includes(s)) return s;
  if ((ota.completion_status || '').toLowerCase() === 'completed' || ota.locked) return 'completed';
  if (ota.timer_running || ota.timer_start) return 'in_progress';
  return 'pending';
};

// iter253 — Airbnb (and MakemyTrip variants that some datasets store as
// "airbnb" only) skip the Under Review stage. Everything else supports it.
const supportsUnderReview = (ota) => {
  const nm = (ota?.ota_name || '').toLowerCase().replace(/\s|-/g, '');
  return !(nm === 'airbnb' || nm === 'airbnbcom');
};

const OTATimerControl = ({ ota, leadId, otaIndex, onUpdate, onReload }) => {
  const status = deriveStatus(ota);
  // iter253 — a listing that has come back from Under Review is on its
  // "second_start" timer; the button that started it is preserved as an
  // action label so the CX Employee (and the CX Leader view) sees the
  // right stage. Backend field: `active_timer_stage`.
  const isSecondStart = (ota?.active_timer_stage || '') === 'second_start' || !!ota?.second_started_at;
  const allowsUnderReview = supportsUnderReview(ota);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'complete' | 'under_review' | 'reject'
  const [hotelId, setHotelId] = useState(ota.hotel_id || '');
  const [listingUrl, setListingUrl] = useState(ota.listing_url || '');
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  // iter253 — pick the correct active-timer anchor.
  //   in_progress + second_start → second_started_at
  //   in_progress + first_start  → started_at (falls back to work_started_at)
  //   under_review               → review_started_at
  const activeTimerAnchor = (() => {
    if (status === 'under_review') return ota.review_started_at || null;
    if (isSecondStart) return ota.second_started_at || ota.timer_start || null;
    return ota.started_at || ota.work_started_at || ota.timer_start || null;
  })();

  // Live elapsed for the active timer (any of the three stages).
  useEffect(() => {
    if ((status === 'in_progress' || status === 'under_review') && activeTimerAnchor) {
      const start = new Date(activeTimerAnchor).getTime();
      const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
      tick();
      intervalRef.current = setInterval(tick, 1000);
      return () => clearInterval(intervalRef.current);
    }
    clearInterval(intervalRef.current);
  }, [status, activeTimerAnchor]);

  const fmt = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
  };

  const handleStart = async () => {
    setLoading(true); setError('');
    try {
      await otaTimerAPI.startTimer(leadId, otaIndex);
      // Item 8 — refresh immediately so the status + action dropdown render
      // without a manual page refresh. Prefer onReload (re-fetch) over the
      // optimistic onUpdate so the row reflects authoritative server state.
      if (onReload) { onReload(); }
      else if (onUpdate) onUpdate(otaIndex, { listing_status: 'in_progress', timer_running: true, timer_start: new Date().toISOString(), work_started_at: ota.work_started_at || new Date().toISOString() });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start');
    }
    setLoading(false);
  };

  const runAction = async (action, payload = {}) => {
    setLoading(true); setError('');
    try {
      const res = await otaTimerAPI.listingAction(leadId, otaIndex, { action, ...payload });
      setModal(null);
      if (onReload) { onReload(); }
      else if (onUpdate) onUpdate(otaIndex, { listing_status: res.data.listing_status, ...payload, ...(action === 'complete' ? { completion_status: 'Completed', locked: true } : {}) });
    } catch (e) {
      setError(e.response?.data?.detail || `Failed to ${action.replace('_', ' ')}`);
    }
    setLoading(false);
  };

  const validUrl = (u) => { try { new URL(u); return true; } catch { return false; } };

  const submitComplete = () => {
    // Phase-1 restructure — CX completion no longer collects Hotel ID / URL
    // (QC captures them). Just mark the listing complete.
    runAction('complete');
  };

  // ---------- Completed (terminal) ----------
  if (status === 'completed') {
    return (
      <div className="flex flex-col gap-1" data-testid={`ota-completed-${otaIndex}`}>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
          <CheckCircle size={13} /> Completed
        </span>
        {ota.listing_url && (
          <a href={ota.listing_url} target="_blank" rel="noreferrer" className="text-xs text-teal-600 hover:underline inline-flex items-center gap-1">
            <ExternalLink size={11} /> View Listing
          </a>
        )}
      </div>
    );
  }

  // ---------- Pending (not started) ----------
  if (status === 'pending') {
    return (
      <div data-testid={`ota-pending-${otaIndex}`}>
        <button onClick={handleStart} disabled={loading} data-testid={`ota-start-${otaIndex}`}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Start
        </button>
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  const meta = STATUS_META[status];
  // Item 7 — a listing rejected twice is FINAL: terminal state, no actions.
  const isFinalRejected = status === 'rejected' && (ota.final_rejected || (ota.reject_count || 0) >= 2);

  // ---------- Finally Rejected (terminal) ----------
  if (isFinalRejected) {
    return (
      <div className="flex flex-col gap-1" data-testid={`ota-final-rejected-${otaIndex}`}>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700">
          <XCircle size={13} /> Rejected (Final)
        </span>
        {(ota.reject_count || 0) > 0 && (
          <span className="text-[10px] text-red-500">×{ota.reject_count} rejections</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid={`ota-control-${otaIndex}`}>
      {/* Status badge + live time + anchor date/time. */}
      <div className="flex items-center gap-2 flex-wrap">
        {meta && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${meta.cls}`} data-testid={`ota-status-badge-${otaIndex}`}>
            <meta.Icon size={11} /> {meta.label}{isSecondStart && status === 'in_progress' ? ' (2nd)' : ''}
          </span>
        )}
        {(status === 'in_progress' || status === 'under_review') && (
          <span className="text-xs text-gray-500 font-mono" data-testid={`ota-elapsed-${otaIndex}`}>{fmt(elapsed)}</span>
        )}
        {(ota.reject_count > 0) && (
          <span className="text-[10px] text-red-500">×{ota.reject_count} rejected</span>
        )}
      </div>
      {/* iter253 — always show BOTH date AND time for the active stage so
          the CX Leader dashboard and the CX employee see the exact clock
          reference. Never just the time on its own. */}
      {activeTimerAnchor && (
        <span className="text-[11px] text-gray-500" data-testid={`ota-stage-timestamp-${otaIndex}`}>
          {(status === 'under_review' ? 'Under Review' : (isSecondStart ? 'Second Start' : 'Started'))}: {formatDateTime(activeTimerAnchor)}
        </span>
      )}

      {/* Actions per state */}
      {status === 'in_progress' && (
        <div className="relative">
          <button onClick={() => setMenuOpen(!menuOpen)} disabled={loading} data-testid={`ota-action-menu-${otaIndex}`}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg hover:bg-slate-700 disabled:opacity-50">
            Update Status <ChevronDown size={12} />
          </button>
          {menuOpen && (
            <div className="absolute z-20 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-testid={`ota-action-dropdown-${otaIndex}`}>
              <button onClick={() => { setMenuOpen(false); setError(''); runAction('complete'); }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-green-50 text-gray-700 flex items-center gap-2" data-testid={`ota-opt-complete-${otaIndex}`}>
                <CheckCircle size={13} className="text-green-600" /> Complete
              </button>
              {/* iter253 — Reject visible during in_progress ONLY when it's the
                  second_start (after under review). Airbnb / first_start still
                  offer Complete only (matches the spec's Start→Complete/Reject
                  transition after the 2nd start). */}
              {isSecondStart && (
                <button onClick={() => { setMenuOpen(false); setError(''); setModal('reject'); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-gray-700 flex items-center gap-2" data-testid={`ota-opt-reject-${otaIndex}`}>
                  <XCircle size={13} className="text-red-600" /> Reject
                </button>
              )}
              {/* iter253 — Under Review hidden for Airbnb AND hidden after
                  the 2nd start (per spec: Under Review is only reachable
                  once, between first Start and second Start). */}
              {allowsUnderReview && !isSecondStart && (
                <button onClick={() => { setMenuOpen(false); setError(''); runAction('under_review'); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-amber-50 text-gray-700 flex items-center gap-2" data-testid={`ota-opt-review-${otaIndex}`}>
                  <Eye size={13} className="text-amber-600" /> Under Review
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* iter253 — Under Review action shows a single "Start" button (the
          SECOND start). Clicking it POSTs to /start-timer which the backend
          treats as second_start when the current status is under_review. */}
      {status === 'under_review' && (
        <div className="flex items-center gap-1.5">
          <button onClick={handleStart} disabled={loading} data-testid={`ota-second-start-${otaIndex}`}
            className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Start
          </button>
        </div>
      )}

      {status === 'rejected' && (
        <div className="flex items-center gap-1.5">
          <button onClick={() => { setError(''); runAction('complete'); }} disabled={loading}
            className="flex items-center gap-1 px-2.5 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50" data-testid={`ota-complete-btn-${otaIndex}`}>
            <CheckCircle size={11} /> Complete
          </button>
          <button onClick={() => { setError(''); setModal('reject'); }} disabled={loading}
            className="flex items-center gap-1 px-2.5 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50" data-testid={`ota-reject-btn-${otaIndex}`}>
            <XCircle size={11} /> Reject
          </button>
        </div>
      )}

      {error && !modal && <p className="text-red-500 text-xs">{error}</p>}

      {/* Modals */}
      {modal === 'reject' && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => !loading && setModal(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`ota-modal-reject-${otaIndex}`}>
            <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2"><XCircle size={18} className="text-red-600 flex-shrink-0" /> Reject Listing</h3>
            <p className="text-sm text-gray-600 mb-2 break-words whitespace-normal">This listing will be marked <strong>Rejected</strong> and the CX Leader &amp; Executive will be notified.</p>
            <p className="text-xs text-amber-600 mb-4 break-words whitespace-normal flex items-start gap-1"><AlertTriangle size={12} className="flex-shrink-0 mt-0.5" /> Rejecting a second time marks the listing as <strong>finally Rejected</strong> — no further actions will be possible.</p>
            {error && <p className="text-red-500 text-xs mb-2">{error}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setModal(null)} disabled={loading} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={() => runAction('reject')} disabled={loading} data-testid={`ota-modal-submit-reject-${otaIndex}`}
                className="px-4 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OTATimerControl;
